<script lang="ts">
  /**
   * People and portals. Owner only.
   *
   * 01-fabric.md §6 is emphatic about what this screen is NOT — "No console, no
   * CLI, and nothing else on the screen." Four rows of people and ten of portals.
   * The estate panels live on their own screens for exactly that reason.
   *
   * Two things have been added and both belong here rather than elsewhere: the
   * last ten claims changes, directly beneath the people table (item 7 —
   * a claims edit that leaves no trace is the problem being solved, so the trace
   * belongs next to the edit), and a per-portal environment drawer (item 4) that
   * answers *what is this thing* without opening the console.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { session } from "../lib/stores.svelte";
  import { stepUp } from "../lib/stepup.svelte";

  type Role = "owner" | "partner" | "display" | "guest";

  type UserRow = {
    uid: string;
    displayName?: string;
    email?: string;
    /** Null for someone who has signed in but never been granted a role. */
    role: Role | null;
    modules?: string[];
    expiresAt?: { _seconds: number } | string | null;
    lastSeenAt?: { _seconds: number } | string | null;
    lastSignInAt?: string | null;
    pending?: boolean;
    revokedAt?: { _seconds: number } | string | null;
  };

  type PortalRow = {
    id: string;
    name: string;
    blurb: string;
    url: string;
    projectId: string;
    healthUrl: string | null;
    minRole: Role;
    theme?: string;
    icon?: string;
    order: number;
    status: "live" | "building" | "planned";
    runService?: string | null;
    hostingSite?: string | null;
    serviceAccount?: string | null;
    searchUrl?: string | null;
    secrets?: string[];
    schedulerJobs?: string[];
    probe?: { kind: string; path: string; expect: { status: number; jsonPath?: string; maxMs: number } } | null;
    health: {
      ok: boolean;
      latencyMs: number;
      checkedAt: string | null;
      consecutiveFailures: number;
      slow?: boolean;
      probeOk?: boolean | null;
      probeDetail?: string | null;
    } | null;
  };

  type Environment = {
    id: string;
    name: string;
    project: string;
    region: string;
    revision: string | null;
    serviceAccount: string | null;
    scaling: { min: number | null; max: number | null };
    lastDeploy: string | null;
    hostingSite: string | null;
    customDomains: { domain: string; certStatus: string | null }[];
    secrets: { declared: string[]; mounted: string[] };
    schedulerJobs: { id: string; schedule: string; state: string }[];
    sources: Record<string, boolean>;
  };

  type AuditRow = { id: string; at: string | null; kind: string; actor: { email: string | null; uid: string }; summary: string };

  /** steyn-design's nine token sets. Kept in step with tokens.css by hand; the
   *  server validates against the same list, so a drift here shows a missing
   *  option rather than a portal with no tokens. */
  const THEMES = ["stone", "basalt", "fog", "slate", "harbour", "ink", "clay", "graphite", "plum"];

  const ROLES: Role[] = ["owner", "partner", "display", "guest"];

  // Invite. Defaults to `partner`: the role an actual household member gets, and
  // the one that makes `owner` a deliberate choice rather than the path of least
  // resistance.
  let inviteEmail = $state("");
  let inviteRole = $state<Role>("partner");
  let inviting = $state(false);

  async function invite(): Promise<void> {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    inviting = true;
    try {
      // Wrapped in stepUp like every other claims change: POST /users/invite is
      // requireStepUp on the server, and granting somebody access is exactly as
      // consequential as changing an existing role.
      let r: { email: string; role: string; alreadyExisted: boolean } | null = null;
      const ok = await stepUp.guard(async () => {
        r = await api.post<{ email: string; role: string; alreadyExisted: boolean }>("/users/invite", {
          email,
          role: inviteRole,
        // Sensible default: everything except the two admin surfaces. Trim it on
        // their row afterwards - starting from nothing means a new person signs
        // in to an empty front door and concludes it is broken.
          modules: MODULES.filter((m) => m !== "fabric" && m !== "hindsight"),
        });
      }, `Adding ${email} as ${inviteRole}`);

      if (!ok || !r) {
        banner = { kind: "warn", text: "Cancelled — nobody was added." };
        return;
      }
      const added = r as { email: string; role: string; alreadyExisted: boolean };
      banner = {
        kind: "ok",
        text: added.alreadyExisted
          ? `${added.email} already existed — their access is now ${added.role}.`
          : `${added.email} added as ${added.role}. They can sign in with Google now.`,
      };
      inviteEmail = "";
      await load();
    } catch (e) {
      banner = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not add that person." };
    } finally {
      inviting = false;
    }
  }

  /** Tile visibility. Ids match the portal registry's doc ids. */
  const MODULES = ["ha", "hq", "screening", "codebots", "vault", "homestead", "waypoint", "hindsight", "frontdoor", "fabric"];

  let users = $state<UserRow[]>([]);
  let portals = $state<PortalRow[]>([]);
  let trail = $state<AuditRow[]>([]);
  let loading = $state(true);
  let banner = $state<{ kind: "ok" | "warn"; text: string } | null>(null);
  let saving = $state<string | null>(null);
  let drawer = $state<string | null>(null);
  let env = $state<{ data: Environment | null; error: string | null; loading: boolean }>({
    data: null, error: null, loading: false,
  });

  async function load() {
    loading = true;
    try {
      const [u, p] = await Promise.all([
        api.get<{ users: UserRow[] }>("/users"),
        api.get<{ portals: PortalRow[] }>("/portals"),
      ]);
      users = u.users;
      portals = p.portals;
      banner = null;
    } catch (e) {
      banner = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not load" };
    } finally {
      loading = false;
    }
    // The trail is a separate, independent load: an audit read failing must not
    // cost you the ability to edit anyone's role.
    await api
      .get<{ entries: AuditRow[] }>("/audit?kind=claims&limit=10")
      .then((r) => (trail = r.entries))
      .catch(() => (trail = []));
  }

  $effect(() => {
    void load();
  });

  /**
   * Take every permission away. Not a delete: the Google account keeps existing
   * and can sign in, and sees nothing. Deleting the account itself stays a
   * deliberate act at the console.
   */
  async function removeAccess(row: UserRow) {
    if (!confirm(`Remove all access for ${row.email ?? row.uid}?\n\nThe account will still exist and will see nothing.`)) return;
    saving = row.uid;
    try {
      const ok = await stepUp.guard(
        () => api.del(`/users/${row.uid}/access`),
        `Removing all access for ${row.email ?? row.uid}`,
      );
      banner = ok
        ? { kind: "ok", text: `${row.email ?? row.uid} now has no access.` }
        : { kind: "warn", text: "Cancelled — access unchanged." };
      await load();
    } catch (e) {
      banner = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not remove access" };
    } finally {
      saving = null;
    }
  }

  async function saveUser(row: UserRow) {
    saving = row.uid;
    try {
      const ok = await stepUp.guard(
        () =>
          api.post("/claims", {
            uid: row.uid,
            role: row.role,
            modules: row.modules ?? [],
            expiresAt: row.role === "guest" ? toIso(row.expiresAt) : null,
          }),
        `Changing ${row.displayName ?? row.uid}'s access`,
      );
      if (!ok) {
        banner = { kind: "warn", text: "Cancelled — nothing changed." };
        await load();
        return;
      }
      // The change only counts once the token carries it. If the owner edited
      // their own row, refresh so the screen reflects reality immediately.
      if (row.uid === session.user?.uid) await session.refresh();
      banner = { kind: "ok", text: `${row.displayName ?? row.uid} updated.` };
      await load();
    } catch (e) {
      banner = { kind: "warn", text: e instanceof ApiError ? e.message : "Save failed" };
      await load();
    } finally {
      saving = null;
    }
  }

  async function savePortal(row: PortalRow) {
    saving = row.id;
    try {
      const ok = await stepUp.guard(
        () =>
          api.put(`/portals/${row.id}`, {
            name: row.name,
            blurb: row.blurb,
            url: row.url,
            projectId: row.projectId,
            healthUrl: row.healthUrl || null,
            minRole: row.minRole,
            theme: row.theme || null,
            icon: row.icon || null,
            order: Number(row.order),
            status: row.status,
            runService: row.runService || null,
            hostingSite: row.hostingSite || null,
            serviceAccount: row.serviceAccount || null,
            searchUrl: row.searchUrl || null,
            secrets: row.secrets ?? [],
            schedulerJobs: row.schedulerJobs ?? [],
            probe: row.probe ?? null,
          }),
        `Saving the ${row.name} registration`,
      );
      banner = ok
        ? { kind: "ok", text: `${row.name} updated.` }
        : { kind: "warn", text: "Cancelled — nothing changed." };
      if (!ok) await load();
    } catch (e) {
      banner = { kind: "warn", text: e instanceof ApiError ? e.message : "Save failed" };
      await load();
    } finally {
      saving = null;
    }
  }

  async function openDrawer(id: string) {
    if (drawer === id) {
      drawer = null;
      return;
    }
    drawer = id;
    env.loading = true;
    try {
      env.data = await api.get<Environment>(`/admin/portals/${id}/environment`);
      env.error = null;
    } catch (e) {
      env.error = e instanceof ApiError ? e.message : "Could not read the environment";
      env.data = null;
    } finally {
      env.loading = false;
    }
  }

  function toggleModule(row: UserRow, m: string) {
    const set = new Set(row.modules ?? []);
    if (set.has(m)) set.delete(m);
    else set.add(m);
    row.modules = [...set];
  }

  function toIso(v: UserRow["expiresAt"]): string | null {
    if (!v) return null;
    if (typeof v === "string") return v;
    return new Date(v._seconds * 1000).toISOString();
  }

  function stamp(v: UserRow["lastSeenAt"]): string {
    if (!v) return "never";
    return dateZA(typeof v === "string" ? v : v._seconds * 1000);
  }
</script>

<div class="wrap">
  {#if banner}
    <div class="attn" class:attn--warn={banner.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true">
        <Icon name={banner.kind === "warn" ? "alert" : "check"} size={12} />
      </span>
      <span class="attn__text">{banner.text}</span>
    </div>
  {/if}

  <!-- ── People ──────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">People</span>
      <span class="sub">{users.length} in the household</span>
    </div>

    <!-- Adding somebody used to be impossible here: claims attach to a uid, and a
         uid does not exist until that person signs in once. So the screen could
         only edit people who were already in, and the obvious task — add your
         wife — was the one thing it could not do. POST /users/invite pre-creates
         the account, so the grant is waiting before they ever arrive. -->
    <form class="invite" onsubmit={(e) => { e.preventDefault(); void invite(); }}>
      <input
        type="email"
        bind:value={inviteEmail}
        placeholder="their@gmail.com"
        aria-label="Email address to invite"
        required
      />
      <select bind:value={inviteRole} aria-label="Role for the new person">
        {#each ROLES as r (r)}<option value={r}>{r}</option>{/each}
      </select>
      <button class="btn-primary" disabled={inviting || !inviteEmail.trim()}>
        {inviting ? "Adding…" : "Add person"}
      </button>
      <span class="sub invite__hint">
        They sign in with Google using this address — access is already granted when they arrive.
      </span>
    </form>

    {#if loading}
      <p class="sub pad">Loading…</p>
    {:else if users.length === 0}
      <p class="sub pad">Nobody yet. Add the first person above.</p>
    {:else}
      {#if users.some((u) => u.pending)}
        <!-- The whole point of listing Firebase Auth rather than only Firestore:
             someone who has signed in but has no role used to be invisible here,
             which made "grant it here" impossible to act on. -->
        <div class="attn attn--warn">
          <span class="attn__badge" aria-hidden="true"><Icon name="user" size={12} /></span>
          <span class="attn__text">
            {users.filter((u) => u.pending).length} account{users.filter((u) => u.pending).length === 1 ? "" : "s"}
            signed in with no role yet — pick a role and Save to grant access.
          </span>
        </div>
      {/if}
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Role</th><th>Modules</th><th>Expires</th><th>Last seen</th><th></th>
            </tr>
          </thead>
          <tbody>
            {#each users as u (u.uid)}
              <tr class:pending={u.pending}>
                <td>
                  <div class="nm">{u.displayName ?? u.uid}</div>
                  <div class="sub">{u.email ?? ""}</div>
                  {#if u.pending}
                    <div class="sub warnish"><Icon name="alert" size={10} /> no access yet</div>
                  {/if}
                </td>
                <td>
                  <select bind:value={u.role} aria-label="Role for {u.displayName ?? u.uid}">
                    {#if u.pending}<option value={null}>— none —</option>{/if}
                    {#each ROLES as r (r)}<option value={r}>{r}</option>{/each}
                  </select>
                </td>
                <td>
                  <div class="mods">
                    {#each MODULES as m (m)}
                      <label class="chip" class:chip--on={(u.modules ?? []).includes(m)}>
                        <input
                          type="checkbox"
                          checked={(u.modules ?? []).includes(m)}
                          onchange={() => toggleModule(u, m)}
                        />
                        {m}
                      </label>
                    {/each}
                  </div>
                </td>
                <td>
                  {#if u.role === "guest"}
                    <input
                      type="date"
                      value={toIso(u.expiresAt)?.slice(0, 10) ?? ""}
                      onchange={(e) => (u.expiresAt = e.currentTarget.value || null)}
                      aria-label="Access expires"
                    />
                  {:else}
                    <span class="sub">—</span>
                  {/if}
                </td>
                <td class="num sub">{stamp(u.lastSeenAt)}</td>
                <td class="uacts">
                  <button class="btn-primary" onclick={() => saveUser(u)} disabled={saving === u.uid || !u.role}>
                    {saving === u.uid ? "Saving…" : u.pending ? "Grant" : "Save"}
                  </button>
                  {#if !u.pending}
                    <button class="lnk" onclick={() => removeAccess(u)} disabled={saving === u.uid}>Remove access</button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <!-- Item 7: the last ten claims changes, right where claims are changed. -->
      {#if trail.length > 0}
        <div class="trail">
          <div class="sub trailhead">Recent access changes</div>
          {#each trail as t (t.id)}
            <div class="trail__r sub">
              <span class="num">{t.at ? dateZA(t.at) : "—"}</span>
              <span class="trail__s">{t.summary}</span>
              <span class="num">{t.actor.email ?? t.actor.uid}</span>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </section>

  <!-- ── Portals ─────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Portals</span>
      <span class="sub">{portals.length} registered</span>
    </div>

    {#if loading}
      <p class="sub pad">Loading…</p>
    {:else}
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>URL</th><th>Health</th><th>Min role</th><th>Theme</th><th>Order</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {#each portals as p (p.id)}
              <tr>
                <td>
                  <button class="nm nmbtn" onclick={() => openDrawer(p.id)} aria-expanded={drawer === p.id}>
                    {p.name}
                    <Icon name={drawer === p.id ? "chevron-down" : "chevron-right"} size={12} />
                  </button>
                  <div class="sub">{p.projectId}</div>
                </td>
                <td><input class="url" bind:value={p.url} aria-label="URL for {p.name}" /></td>
                <td>
                  <!-- glyph + label + colour, never colour alone. Four states now:
                       not checked, live, live but slow, not responding. -->
                  {#if p.health === null}
                    <span class="status status--idle"><Icon name="pause" size={12} /> not checked</span>
                  {:else if p.health.ok && p.health.slow}
                    <span class="status status--warn" title={p.health.probeDetail ?? ""}>
                      <Icon name="clock" size={12} /> live · slow
                      <span class="num">{p.health.latencyMs}ms</span>
                    </span>
                  {:else if p.health.ok}
                    <span class="status status--ok">
                      <Icon name="check" size={12} /> live
                      <span class="num">{p.health.latencyMs}ms</span>
                    </span>
                  {:else}
                    <span class="status status--warn" title={p.health.probeDetail ?? ""}>
                      <Icon name="alert" size={12} /> not responding
                    </span>
                  {/if}
                </td>
                <td>
                  <select bind:value={p.minRole} aria-label="Minimum role for {p.name}">
                    {#each ROLES as r (r)}<option value={r}>{r}</option>{/each}
                  </select>
                </td>
                <td>
                  <!-- Per-portal, because the household asked for it. The portal
                       reads this at boot from an unauthenticated endpoint, so a
                       change here lands on the next load with no rebuild. -->
                  <select bind:value={p.theme} aria-label="Theme for {p.name}">
                    <option value="">stone (default)</option>
                    {#each THEMES as t (t)}<option value={t}>{t}</option>{/each}
                  </select>
                </td>
                <td><input class="ord num" type="number" bind:value={p.order} aria-label="Order" /></td>
                <td>
                  <select bind:value={p.status} aria-label="Status for {p.name}">
                    <option value="live">live</option>
                    <option value="building">building</option>
                    <option value="planned">planned</option>
                  </select>
                </td>
                <td>
                  <button class="btn-primary" onclick={() => savePortal(p)} disabled={saving === p.id}>
                    {saving === p.id ? "Saving…" : "Save"}
                  </button>
                </td>
              </tr>

              {#if drawer === p.id}
                <tr class="drawer">
                  <td colspan="7">
                    <div class="dgrid">
                      <label>
                        <span class="lb">Health URL</span>
                        <input bind:value={p.healthUrl} placeholder="https://…/health" />
                      </label>
                      <label>
                        <span class="lb">Cloud Run service</span>
                        <input bind:value={p.runService} placeholder="vault-api" />
                      </label>
                      <label>
                        <span class="lb">Hosting site</span>
                        <input bind:value={p.hostingSite} placeholder="steyn-vault" />
                      </label>
                      <label>
                        <span class="lb">Search endpoint</span>
                        <input bind:value={p.searchUrl} placeholder="https://…/search" />
                      </label>
                      <label class="wide">
                        <span class="lb">Blurb</span>
                        <input bind:value={p.blurb} />
                      </label>
                    </div>
                    <p class="sub dnote">
                      Registering these is what fills this card in and what lets the equivalent prose leave the
                      README — two sources of truth means one of them is stale.
                    </p>

                    {#if env.loading}
                      <p class="sub pad">Reading the environment…</p>
                    {:else if env.error}
                      <p class="sub pad"><Icon name="alert" size={12} /> {env.error}</p>
                    {:else if env.data}
                      {@const e = env.data}
                      <div class="facts">
                        {@render fact("Project", e.project)}
                        {@render fact("Region", e.region)}
                        {@render fact("Revision", e.revision ?? "—")}
                        {@render fact("Runtime identity", e.serviceAccount ?? "—")}
                        {@render fact("Scaling", e.scaling.min === null ? "—" : `min ${e.scaling.min} / max ${e.scaling.max ?? "—"}`)}
                        {@render fact("Last deploy", e.lastDeploy ? dateZA(e.lastDeploy) : "—")}
                        {@render fact("Hosting site", e.hostingSite ?? "—")}
                        {@render fact(
                          "Custom domains",
                          e.customDomains.length === 0 ? "none (web.app only)" : e.customDomains.map((d) => `${d.domain} ${d.certStatus ?? ""}`).join(", "),
                        )}
                        {@render fact("Secrets mounted", e.secrets.mounted.join(", ") || "none")}
                        {@render fact(
                          "Scheduler jobs",
                          e.schedulerJobs.length === 0 ? "none" : e.schedulerJobs.map((j) => `${j.id} (${j.schedule})`).join(", "),
                        )}
                      </div>
                      {#if !e.sources.run}
                        <p class="sub dnote">
                          <Icon name="pause" size={12} /> No Cloud Run service is registered, so the revision and
                          scaling rows read as —. Name one above to fill them.
                        </p>
                      {/if}
                    {/if}
                  </td>
                </tr>
              {/if}
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>
</div>

{#snippet fact(label: string, value: string)}
  <div class="fact">
    <div class="lb">{label}</div>
    <div class="fact__v">{value}</div>
  </div>
{/snippet}

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .card {
    padding: 14px 16px 16px;
  }
  .sec {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding-bottom: 10px;
  }
  .pad {
    padding: 8px 0 2px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Wide tables scroll inside their own container; the page body never does. */
  .scroll {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.7812rem;
  }
  th {
    text-align: left;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
    padding: 6px 8px;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }
  td {
    padding: 9px 8px;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  .nm {
    font-weight: 700;
    color: var(--tx);
  }
  tr.pending {
    background: var(--fill);
  }
  .warnish {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--warn);
  }
  .uacts {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }
  .lnk {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
    white-space: nowrap;
  }
  .lnk:hover {
    color: var(--warn);
  }
  .nmbtn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--tx);
    font-weight: 700;
    font-size: 0.7812rem;
  }
  .nmbtn:hover {
    color: var(--acc);
  }
  select,
  input {
    background: var(--fill);
    color: var(--tx);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
    padding: 5px 7px;
    font-size: 0.75rem;
  }
  .url {
    min-width: 230px;
    width: 100%;
  }
  .ord {
    width: 62px;
  }
  .invite {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 2px 0 12px;
  }
  .invite input[type="email"] {
    min-width: 220px;
  }
  .invite__hint {
    flex-basis: 100%;
  }
  .mods {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 330px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: var(--r-pill);
    border: 1px solid var(--line);
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
    cursor: pointer;
  }
  .chip--on {
    color: var(--tx);
    background: var(--wash);
    border-color: var(--acc);
  }
  .chip input {
    /* The chip itself is the affordance; the box would be a second one. */
    appearance: none;
    width: 0;
    height: 0;
    margin: 0;
    padding: 0;
    border: none;
    position: absolute;
  }
  .chip:focus-within {
    outline: 2px solid var(--acc);
    outline-offset: 1px;
  }
  button:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .trail {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--line);
  }
  .trailhead {
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 5px;
  }
  .trail__r {
    display: flex;
    gap: 10px;
    padding: 3px 0;
    align-items: baseline;
  }
  .trail__s {
    flex: 1;
    color: var(--tx2);
  }

  .drawer td {
    background: var(--fill);
  }
  .dgrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
  }
  .dgrid label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .dgrid .wide {
    grid-column: 1 / -1;
  }
  .dnote {
    margin: 10px 0 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 10px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--line);
  }
  .fact__v {
    font-size: 0.75rem;
    color: var(--tx);
    overflow-wrap: anywhere;
  }
</style>
